import axios from 'axios';
import { Download, Trash2, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const BrochureManager = () => {
  const [brochures, setBrochures] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchBrochures();
  }, []);

  const fetchBrochures = async () => {
    try {
      const response = await axios.get(`${API}/brochures`);
      setBrochures(response.data);
    } catch (error) {
      console.error('Error fetching brochures:', error);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', selectedFile);
    uploadFormData.append('title', formData.title);
    uploadFormData.append('description', formData.description);
    uploadFormData.append('category', formData.category);

    try {
      await axios.post(`${API}/brochures/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Brochure uploaded successfully');
      setFormData({ title: '', description: '', category: '' });
      setSelectedFile(null);
      fetchBrochures();
    } catch (error) {
      toast.error('Failed to upload brochure');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (brochure_id, filename) => {
    try {
      const response = await axios.get(`${API}/brochures/download/${brochure_id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download brochure');
    }
  };

  const handleDelete = async (brochure_id) => {
    if (window.confirm('Are you sure you want to delete this brochure?')) {
      try {
        await axios.delete(`${API}/brochures/${brochure_id}`);
        toast.success('Brochure deleted');
        fetchBrochures();
      } catch (error) {
        toast.error('Failed to delete brochure');
      }
    }
  };

  return (
    <div data-testid="brochure-manager">
      <h1 className="font-heading text-3xl font-medium text-white mb-8">Brochure Manager</h1>

      <div className="bg-slate-900 border border-white/10 p-8 mb-8">
        <h2 className="text-xl font-semibold text-white mb-6">Upload New Brochure</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Brochure Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
            />
            <input
              type="text"
              placeholder="Category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
              className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
            />
          </div>
          <textarea
            placeholder="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows="3"
            className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 p-4 text-white placeholder:text-slate-500 outline-none resize-none"
          />
          <div>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.ppt,.pptx"
              onChange={handleFileChange}
              required
              className="w-full bg-slate-900/50 border border-white/10 focus:border-ocean-500 p-3 text-white outline-none"
            />
            {selectedFile && (
              <p className="text-sm text-slate-400 mt-2">Selected: {selectedFile.name}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={uploading}
            className="px-6 py-3 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Upload size={16} />
            {uploading ? 'Uploading...' : 'Upload Brochure'}
          </button>
        </form>
      </div>

      <div className="space-y-4">
        {brochures.map((brochure) => (
          <div
            key={brochure.brochure_id}
            className="bg-slate-900 border border-white/10 p-6 flex items-center justify-between"
          >
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">{brochure.title}</h3>
              <p className="text-sm text-slate-400 mb-2">{brochure.description}</p>
              <div className="flex gap-3 text-xs">
                <span className="px-2 py-1 bg-slate-800 text-slate-300">{brochure.category}</span>
                <span className="text-slate-500">{brochure.filename}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleDownload(brochure.brochure_id, brochure.filename)}
                className="p-2 border border-white/10 hover:border-ocean-500 text-slate-400 hover:text-white transition-colors"
              >
                <Download size={18} />
              </button>
              <button
                onClick={() => handleDelete(brochure.brochure_id)}
                className="p-2 border border-white/10 hover:border-red-500 text-slate-400 hover:text-red-500 transition-colors"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BrochureManager;
